import React, { FC, useEffect, useState, startTransition } from 'react';
import { useSnackbar } from 'src/view/components/SnackbarService';
import { useAppState } from 'src/state-management/state-provider';
import FullPageMessage from 'src/view/components/FullPageMessage';
import FullPageLoader from 'src/view/components/FullPageLoader';
import { Home } from '@mui/icons-material';
import clientService from 'src/clientService';
import { ProjectStatusEnum } from 'src/api/Api';

interface MapFetcherWrapperProps {
  children: React.ReactNode;
}

const MapFetcherWrapper: FC<MapFetcherWrapperProps> = ({ children }) => {
  const { state, dispatch } = useAppState();
  const { showSnackbar, snackbarType } = useSnackbar();

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const projectDetails = state.selectedProject?.projectDetails;

  useEffect(() => {
    if (!projectDetails) {
      // Assumes ProjectDetailsFetcherWrapper has already run and is handling the initial loading.
      // If projectDetails are still not available, it might be loading or have failed.
      // This wrapper will wait for projectDetails.
      return;
    }

    if (projectDetails.project_status !== ProjectStatusEnum.UnderDevelopment) {
      startTransition(() => {
        setError(
          'Project is not in the "Under development" phase. Map data is not available.',
        );
        setIsLoading(false);
      });
      return;
    }

    if (
      state.selectedProject?.bioMapData &&
      state.selectedProject.bioMapData.sites?.[0]?.project_id ===
        projectDetails.project_id
    ) {
      setIsLoading(false);
      return;
    }

    const projectId = projectDetails.project_id;
    if (projectId) {
      setIsLoading(true);
      setError(null);

      clientService.api
        .getProjectMapDataApiV1ProjectsProjectIdMapGet(projectId)
        .then((response) => {
          startTransition(() => {
            dispatch({
              type: 'SET_PROJECT_MAP_DATA',
              payload: response.data,
            });
            setIsLoading(false);
          });
        })
        .catch((apiError) => {
          console.error('Failed to fetch project map data:', apiError);
          const errorMessage =
            apiError.response?.data?.detail ||
            `Failed to load project map data for ID: ${projectId}.`;
          startTransition(() => {
            setError(errorMessage);
            showSnackbar({
              message: 'Error fetching project map data.',
              type: snackbarType.ERROR,
            });
            setIsLoading(false);
          });
        });
    } else {
      startTransition(() => {
        setError('Project ID is missing in the route parameters.');
        showSnackbar({
          message: 'Project ID missing.',
          type: snackbarType.ERROR,
        });
        setIsLoading(false);
      });
    }
  }, [projectDetails, state.selectedProject?.bioMapData]);

  if (isLoading) {
    return (
      <FullPageLoader
        title="Loading map data..."
        message="Please wait while we fetch the map details."
      />
    );
  }

  if (error) {
    return (
      <FullPageMessage
        headline="Could not load map data."
        subHeadline={error}
        ctaText="Go to Home"
        onCtaClick={() => {
          window.location.href = '/';
        }}
        ctaIcon={<Home />}
      />
    );
  }

  return <>{children}</>;
};

export default MapFetcherWrapper;
